/* ─────────────────────────────────────────────────────────
 *  MIDI DAW – Project Snapshot
 *
 *  A snapshot is the portable, versionable unit of a project:
 *  the full parsed MIDI file plus the per-track synth patches.
 *  It is what gets serialized to GCS (server-side) and loaded
 *  back into the DAW on demand.
 *
 *  Single Responsibility: build/parse the snapshot envelope.
 *  No I/O — pure transforms. The hook owns persistence.
 * ────────────────────────────────────────────────────────── */
import type { DawMidiFile, DawSynthPatch } from '../types/midi-daw-types'

/** Bump when the snapshot shape changes in a non-back-compatible way. */
export const SNAPSHOT_VERSION = 1

export interface MidiProjectSnapshot {
  /** Snapshot schema version (forward-compat guard). */
  snapshotVersion: number
  /** ISO timestamp of when the snapshot was captured (client clock). */
  savedAt: string
  /** The full parsed MIDI file (tracks, notes, tempo, time-sig). */
  midiFile: DawMidiFile
  /** Per-track synth patch assignments (keyed by track index). */
  patches: Record<number, DawSynthPatch>
}

/** Build a snapshot envelope from current DAW state. */
export function buildSnapshot(
  midiFile: DawMidiFile,
  patches: Record<number, DawSynthPatch>,
): MidiProjectSnapshot {
  return {
    snapshotVersion: SNAPSHOT_VERSION,
    savedAt: new Date().toISOString(),
    midiFile,
    patches: patches ?? {},
  }
}

/**
 * Parse a snapshot returned by the BFF back into DAW-loadable parts.
 * Tolerates legacy shapes where the payload was a bare DawMidiFile.
 */
export function parseSnapshot(raw: unknown): { midiFile: DawMidiFile; patches: Record<number, DawSynthPatch> } | null {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Partial<MidiProjectSnapshot> & Partial<DawMidiFile>

  // Modern envelope.
  if (obj.midiFile && typeof obj.midiFile === 'object') {
    return {
      midiFile: obj.midiFile as DawMidiFile,
      patches: (obj.patches as Record<number, DawSynthPatch>) ?? {},
    }
  }

  // Legacy: payload was the DawMidiFile itself.
  if (Array.isArray((obj as DawMidiFile).tracks)) {
    return { midiFile: obj as DawMidiFile, patches: {} }
  }

  return null
}
